import { useCallback, useEffect, useRef, useState } from 'react';
import { storyBibleApi } from '../api/storyBible';

/**
 * useStoryBible — lazy-loads a story's Bible for the scene inspector's
 * character picker (bind-only: the Story Board never creates or edits Bible
 * entries, only references them). `ensureLoaded()` fetches once per story
 * and caches for the lifetime of this hook instance; call it again after a
 * storyId change to load the new story. A 404 (no Bible yet) is treated as
 * an empty state, not an error — matches StoryBibleEditor.jsx's own
 * handling of the same endpoint.
 */
export default function useStoryBible(storyId) {
  const [bible, setBible] = useState(null); // null = not loaded yet
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadedForRef = useRef(null);

  useEffect(() => {
    // Story changed — drop the cache so the next ensureLoaded() re-fetches.
    loadedForRef.current = null;
    setBible(null);
    setError(null);
  }, [storyId]);

  const ensureLoaded = useCallback(async () => {
    if (!storyId || loadedForRef.current === storyId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await storyBibleApi.get(storyId);
      setBible(data);
      loadedForRef.current = storyId;
    } catch (err) {
      if (err?.status === 404) {
        setBible({ sections: [] });
        loadedForRef.current = storyId;
      } else {
        setError(err?.message || 'Failed to load Book Notes.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [storyId]);

  // Flat list of confirmed, named entries across every section, filtered to
  // actual characters and tagged with a display group label.
  //
  // Before Book Sheet Cards, this hook had no real category concept: every
  // confirmed+named entry across the whole Bible was treated as a pickable
  // "character," grouped by whichever section it happened to live in. Now
  // that Book Sheet stamps a real `category` on its rows, that old
  // behavior would wrongly surface a named World or Theme card as a
  // pickable character. So:
  //
  // - Filter: an entry with a `category` must be category === 'Character'
  //   (case-insensitive) to qualify. An entry with NO category (every row
  //   written before this migration, or by a path that never set one) is
  //   legacy data — kept under the old "every confirmed+named entry is a
  //   character" rule, so existing test/harness data doesn't just vanish
  //   from the picker.
  // - Group label: ALL Book Sheet cards land in one "Unsorted"
  //   `story_bible_sections` row (`ensure_unsorted_section`), so grouping
  //   by `section.title` the old way would now collapse every categorized
  //   character into one useless "Unsorted" heading. Group by `category`
  //   instead (all rows that pass the filter above share category
  //   'Character', so this heading reads "Character"); legacy rows with no
  //   category keep grouping by `section.title`, unchanged from before.
  const confirmedEntries = (bible?.sections || []).flatMap((section) => (
    (section.entries || [])
      .filter((entry) => entry.confirmed && (entry.name || '').trim())
      .filter((entry) => {
        const category = (entry.category || '').trim();
        return !category || category.toLowerCase() === 'character';
      })
      .map((entry) => ({
        ...entry,
        sectionTitle: section.title,
        groupLabel: (entry.category || '').trim() || section.title,
      }))
  ));

  return { confirmedEntries, isLoading, error, ensureLoaded };
}
